import React, { useEffect, useState } from "react";
import { FiPlus, FiEdit2, FiTrash2, FiImage, FiCheck, FiX, FiRefreshCw } from "react-icons/fi";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PROPERTY_TYPES_KEYS = [
  { label: "Home / House", value: "Home" },
  { label: "Apartment / Flat", value: "apartment" },
  { label: "Townhouse", value: "townhouse" },
  { label: "Land / Plot", value: "land" },
  { label: "Commercial Property", value: "other-commercial" },
  { label: "Penthouse", value: "penthouse" },
  { label: "Studio", value: "studio" },
  { label: "Villa", value: "villa" },
  { label: "Office", value: "office" },
  { label: "Showroom", value: "showroom" },
  { label: "Shop", value: "shop" },
];

export default function PropertyCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Delete Confirmation Modal
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });

  // Form State
  const [form, setForm] = useState({
    name: "",
    propertyTypeKey: "Home",
    propertySector: "residential",
    categoryType: "both",
    image: "",
    status: true,
  });

  // Fetch Categories from Backend
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/admin/getPropertyCategoryList", {});
      if (res?.data?.list) {
        setCategories(res.data.list);
      } else {
        setCategories([]);
      }
    } catch (err) {
      showErrorToast("Failed to fetch property categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle Form Change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

   // Image Upload Handler
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploading(true);
      
      const res = await uploadFiles({ files, endpoint: '/uploadMultipleImages' });
      const resData = res?.data || res;
      const firstItem = Array.isArray(resData) ? resData[0] : resData;
      
      // 👇 Object me se direct string imageUrl extract karein 👇
      const imageUrl = typeof firstItem === 'object' ? (firstItem?.imageUrl || firstItem?.url) : firstItem;

      if (imageUrl && typeof imageUrl === 'string') {
        setForm((prev) => ({ ...prev, image: imageUrl }));
        showSuccessToast("Image uploaded successfully!");
      } else {
        showErrorToast("Failed to upload image");
      }
    } catch (err) {
      showErrorToast(err?.message || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };



  // Open Add Modal
  const openAddModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setForm({
      name: "",
      propertyTypeKey: "Home",
      categoryType: "both",
      image: "",
      status: true,
    });
    setModalOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (cat) => {
    setIsEditing(true);
    setEditingId(cat._id);
    setForm({
      name: cat.name || "",
      propertyTypeKey: cat.propertyTypeKey || "Home",
      categoryType: cat.categoryType || "both",
      image: cat.image || "",
      status: cat.status !== undefined ? cat.status : true,
    });
    setModalOpen(true);
  };

  // Save Category (Add or Edit)
  // Save Category (Add or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      return showErrorToast("Please enter Category Name");
    }

    try {
      setLoading(true);

      // Auto Set propertyTypeKey from Category Name & Default categoryType = "both"
      const payload = {
        ...form,
        propertyTypeKey: form.name.trim().toLowerCase(), // Auto Key from Name
        categoryType: form.categoryType, // Auto Both Buy & Rent
      };  

      if (isEditing) {
        const res = await makeRequestAuth("post", "/admin/updatePropertyCategory", {
          id: editingId,
          ...payload,
        });
        if (res?.data) {
          showSuccessToast("Category updated successfully!");
          setModalOpen(false);
          fetchCategories();
        }
      } else {
        const res = await makeRequestAuth("post", "/admin/addPropertyCategory", payload);
        if (res?.data) {
          showSuccessToast("Category created successfully!");
          setModalOpen(false);
          fetchCategories();
        }
      }
    } catch (err) {
      showErrorToast(err?.message || "Failed to save category");
    } finally {
      setLoading(false);
    }
  };


  // Toggle Category Active Status directly
  const handleToggleStatus = async (cat) => {
    try {
      await makeRequestAuth("post", "/admin/updatePropertyCategory", {
        id: cat._id,
        status: !cat.status,
      });
      showSuccessToast(`Status updated to ${!cat.status ? 'Active' : 'Inactive'}`);
      fetchCategories();
    } catch (err) {
      showErrorToast("Failed to update status");
    }
  };

  // Delete Category
  const confirmDelete = async () => {
    try {
      setLoading(true);
      await makeRequestAuth("post", "/admin/deletePropertyCategory", {
        id: deleteModal.id,
      });
      showSuccessToast("Category deleted successfully!");
      setDeleteModal({ open: false, id: null, name: "" });
      fetchCategories();
    } catch (err) {
      showErrorToast("Failed to delete category");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <ToastContainer />

      {/* HEADER SECTION */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Property Categories</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage dynamic property type cards shown on Website Homepage (Buy & Rent)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchCategories}
            className="p-2.5 text-gray-600 hover:text-[#24ADD7] bg-gray-50 rounded-xl hover:bg-blue-50 transition"
            title="Refresh List"
          >
            <FiRefreshCw size={18} />
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 bg-[#24ADD7] hover:bg-[#1a95bb] text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-md transition-all active:scale-95"
          >
            <FiPlus size={18} />
            Add New Category
          </button>
        </div>
      </div>

      {/* CATEGORIES TABLE LIST */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-20 text-gray-400 font-semibold">
            Loading Categories...
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-16 px-4">
            <FiImage size={40} className="mx-auto text-gray-300 mb-3" />
            <h3 className="text-lg font-bold text-gray-700">No Property Categories Found</h3>
            <p className="text-sm text-gray-400 mt-1">Click "+ Add New Category" above to create your first dynamic card.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  <th className="py-4 px-6">Preview Image</th>
                  <th className="py-4 px-6">Category Name</th>
                  <th className="py-4 px-6">Search Type Key</th>
                  <th className="py-4 px-6">Applies To</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm font-semibold text-gray-700">
                {categories.map((cat) => (
                  <tr key={cat._id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="py-4 px-6">
                      {cat.image ? (
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-16 h-12 object-cover rounded-xl border border-gray-100 shadow-xs"
                        />
                      ) : (
                        <div className="w-16 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-xs">
                          No Image
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6 font-bold text-gray-900 uppercase">{cat.name}</td>
                    <td className="py-4 px-6">
                      <span className="bg-blue-50 text-[#24ADD7] px-3 py-1 rounded-lg text-xs font-bold">
                        {cat.propertyTypeKey || "Home"}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase ${
                        cat.categoryType === 'buy' ? 'bg-emerald-50 text-emerald-600' :
                        cat.categoryType === 'rent' ? 'bg-purple-50 text-purple-600' :
                        'bg-orange-50 text-orange-600'
                      }`}>
                        {cat.categoryType === 'both' ? 'Buy & Rent' : cat.categoryType}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <button
                        onClick={() => handleToggleStatus(cat)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                          cat.status
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "bg-red-100 text-red-700 hover:bg-red-200"
                        }`}
                      >
                        {cat.status ? <FiCheck /> : <FiX />}
                        {cat.status ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(cat)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, id: cat._id, name: cat.name })}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD / EDIT MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center border-b pb-3">
              <h2 className="text-xl font-bold text-gray-800">
                {isEditing ? "Edit Property Category" : "Add New Property Category"}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Category Name */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">
                  Category Title / Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. HOUSE, APARTMENT, VILLAS"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-[#24ADD7]"
                  required
                />
              </div>
              {/* Property Sector (Residential / Commercial) */}
<div>
  <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">
    Property Sector <span className="text-red-500">*</span>
  </label>
  <select
    name="propertySector"
    value={form.propertySector}
    onChange={handleChange}
    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-[#24ADD7]"
  >
    <option value="residential">Residential</option>
    <option value="commercial">Commercial</option>
    <option value="both">Both (Residential & Commercial)</option>
  </select>
</div>
{/* Category Type / Listing Type (Buy, Rent, Both) */}
<div>
  <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">
    Category Type (Applies To) <span className="text-red-500">*</span>
  </label>
  <select
    name="categoryType"
    value={form.categoryType}
    onChange={handleChange}
    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-[#24ADD7]"
  >
    <option value="both">Both (Buy & Rent)</option>
    <option value="buy">Buy Only</option>
    <option value="rent">Rent Only</option>
  </select>
</div>


              {/* Property Search Type Key */}
            

              {/* Card Image Upload */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1.5">
                  Card Background Image
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-blue-50 file:text-[#24ADD7] hover:file:bg-blue-100 cursor-pointer"
                  />
                  {uploading && <span className="text-xs text-blue-500 font-semibold animate-pulse">Uploading...</span>}
                </div>
                {form.image && (
                  <div className="mt-3 relative w-32 h-20 rounded-xl overflow-hidden border">
                    <img src={form.image} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              {/* Status Toggle */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="status"
                  name="status"
                  checked={form.status}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#24ADD7] rounded border-gray-300"
                />
                <label htmlFor="status" className="text-sm font-bold text-gray-700 cursor-pointer">
                  Active (Show on Website)
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl border text-sm font-bold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-6 py-2.5 rounded-xl bg-[#24ADD7] hover:bg-[#1994bb] text-white text-sm font-bold shadow-md transition"
                >
                  {isEditing ? "Save Changes" : "Create Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4">
            <h3 className="text-lg font-bold text-gray-900">Confirm Delete</h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete category <strong>"{deleteModal.name}"</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setDeleteModal({ open: false, id: null, name: "" })}
                className="px-4 py-2 border rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold shadow-md transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
